import { format } from 'date-fns'
import { SelectionField, User, VisibilityRule, VisibilityRuleOperator } from '../../generated'
import { capitalizeCase, isAttorneyScopedUser, isParalegalScopedUser } from '.'

/**
 * The selection field that scopes a report to one responsible attorney. An attorney-scoped user
 * may only run reports that declare it, and only with their own id in it.
 *
 * Which reports an attorney may see is decided entirely by the API — `getReportTemplates` returns
 * an already-filtered list, driven by the `hideFromAttorney` flags and the Resp 1 field on the
 * templates themselves. The portal deliberately does not re-derive that rule: a second copy here
 * could only ever drift from the server's, and the server is what actually enforces it.
 */
export const ATTORNEY_SCOPE_FIELD = 'responsibleAttorney1'

/** The selection field that scopes Matter List by Attorney / Paralegal to one paralegal (HCK-292). */
export const PARALEGAL_SCOPE_FIELD = 'paralegal'

/**
 * The field this user's own id is locked into on the selected report: Resp 1 for an attorney, the
 * Paralegal filter for a paralegal, or null when the user is not scoped or the report does not declare
 * that field. Which reports the user gets at all is still the API's call (see ATTORNEY_SCOPE_FIELD).
 */
export const ownScopeField = (user: User | null, fields: (Pick<SelectionField, 'field'> | null)[]): string | null => {
  const field = isAttorneyScopedUser(user)
    ? ATTORNEY_SCOPE_FIELD
    : isParalegalScopedUser(user)
    ? PARALEGAL_SCOPE_FIELD
    : null

  return field && fields.some(f => f?.field === field) ? field : null
}

const humanizeKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim()

const isIsoDate = (val: string) => /^\d{4}-\d{2}-\d{2}/.test(val)

const tryFormatDate = (val: string) => {
  try {
    // Plain YYYY-MM-DD strings are parsed as UTC midnight by the JS spec.
    // Appending T00:00:00 forces local-time interpretation so users in
    // negative-offset timezones (e.g. EST = UTC-5) don't see the date
    // shifted back to the previous day.
    const date = /^\d{4}-\d{2}-\d{2}$/.test(val) ? new Date(val + 'T00:00:00') : new Date(val)
    return format(date, 'MMM dd, yyyy')
  } catch {
    return val
  }
}

export const getSelectionFieldEntries = (
  fields: Record<string, any> | null | undefined
): Array<{ label: string; value: string }> => {
  if (!fields) return []
  const entries: Array<{ label: string; value: string }> = []

  for (const [key, val] of Object.entries(fields)) {
    if (val === null || val === undefined || val === '') continue
    const label = humanizeKey(key)

    if (val && typeof val === 'object' && !Array.isArray(val)) {
      if ('start' in val && 'end' in val) {
        const s = isIsoDate(String(val.start)) ? tryFormatDate(String(val.start)) : String(val.start)
        const e = isIsoDate(String(val.end)) ? tryFormatDate(String(val.end)) : String(val.end)
        entries.push({ label, value: `${s} – ${e}` })
      } else {
        const from = val.gte ?? val.gt
        const to = val.lte ?? val.lt
        const fStr =
          from != null ? (isIsoDate(String(from)) ? tryFormatDate(String(from)) : String(from)) : null
        const tStr =
          to != null ? (isIsoDate(String(to)) ? tryFormatDate(String(to)) : String(to)) : null
        if (fStr && tStr) entries.push({ label, value: `${fStr} – ${tStr}` })
        else if (tStr) entries.push({ label, value: tStr })
        else if (fStr) entries.push({ label, value: fStr })
      }
    } else if (typeof val === 'boolean') {
      entries.push({ label, value: val ? 'Yes' : 'No' })
    } else {
      const s = String(val)
      entries.push({ label, value: isIsoDate(s) ? tryFormatDate(s) : s })
    }
  }

  return entries
}

export function isSelectionFieldVisible(field: SelectionField, formValues: Record<string, unknown>): boolean {
  if (!field.visibilityRules?.length) {
    return true
  }

  // All rules must pass for field to be visible
  return field.visibilityRules.every(rule => evaluateRule(rule, formValues))
}

function evaluateRule(rule: VisibilityRule, formValues: Record<string, unknown>): boolean {
  const { dependsOn, value, operator } = rule
  const fieldValue = formValues[dependsOn]

  switch (operator) {
    case VisibilityRuleOperator.Equals:
      return fieldValue === value

    case VisibilityRuleOperator.NotEquals:
      return fieldValue !== value

    case VisibilityRuleOperator.In:
      return Array.isArray(value) && value.includes(fieldValue)

    case VisibilityRuleOperator.NotIn:
      return Array.isArray(value) && !value.includes(fieldValue)

    case VisibilityRuleOperator.Exists:
      return fieldValue !== null && fieldValue !== undefined && fieldValue !== ''

    case VisibilityRuleOperator.NotExists:
      return fieldValue === null || fieldValue === undefined || fieldValue === ''

    default:
      console.warn(`Unknown visibility rule operator: ${operator}`)
      return true
  }
}

export const formatForBalanceSheet = (fields: Record<string, any>) => {
  const formattedFields: string[] = []
  if (fields.asOfDate?.lte) {
    formattedFields.push(`As of: ${format(new Date(fields.asOfDate.lte), 'MMM dd, yyyy')}`)
  }

  if (fields.compareWith === 'previous month' || fields.compareWith === 'previous year') {
    formattedFields.push(`Compared with: ${capitalizeCase(fields.compareWith)}`)
  }

  if (fields.compareWith === 'custom date' && fields.customDate?.lte) {
    formattedFields.push(`Compared date: ${format(new Date(fields.customDate.lte), 'MMM dd, yyyy')}`)
  }

  return formattedFields.join('<br/>')
}

export const formatForIncomeStatement = (fields: Record<string, any>) => {
  const formattedFields: string[] = []
  if (fields.dateRange?.start && fields.dateRange?.end) {
    formattedFields.push(
      `Period: ${format(new Date(fields.dateRange.start), 'MMM dd, yyyy')} - ${format(
        new Date(fields.dateRange.end),
        'MMM dd, yyyy'
      )}`
    )
  }

  if (fields.compareWith === 'same period last year' || fields.compareWith === 'full last year') {
    formattedFields.push(`Compared with: ${capitalizeCase(fields.compareWith)}`)
  }

  if (fields.compareWith === 'custom period' && fields.customDateRange?.start && fields.customDateRange?.end) {
    formattedFields.push(
      `Compared period: ${format(new Date(fields.customDateRange.start), 'MMM dd, yyyy')} - ${format(
        new Date(fields.customDateRange.end),
        'MMM dd, yyyy'
      )}`
    )
  }

  return formattedFields.join('<br/>')
}

const TRIAL_BALANCE_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const TRIAL_BALANCE_QUARTER_MONTHS: Record<string, [number, number]> = {
  Q1: [0, 2],
  Q2: [3, 5],
  Q3: [6, 8],
  Q4: [9, 11]
}

/**
 * Resolves the Trial Balance period filter (periodType + year + quarter/month, or customPeriod)
 * into concrete start/end dates and a short human label. Mirrors buildTrialBalanceQuery so the
 * Report History period matches what was actually run.
 */
export const resolveTrialBalancePeriod = (
  fields: Record<string, any>
): { start: Date | null; end: Date | null; label: string } => {
  const periodType = fields.periodType

  if (periodType === 'Custom Period') {
    return {
      start: fields.customPeriod?.start ? new Date(fields.customPeriod.start) : null,
      end: fields.customPeriod?.end ? new Date(fields.customPeriod.end) : null,
      label: 'Custom'
    }
  }

  const year = Number(fields.year)
  if (!year) return { start: null, end: null, label: '' }

  if (periodType === 'Yearly') {
    return { start: new Date(year, 0, 1), end: new Date(year, 11, 31), label: `${year}` }
  }

  if (periodType === 'Quarterly' && TRIAL_BALANCE_QUARTER_MONTHS[fields.quarter]) {
    const [startMonth, endMonth] = TRIAL_BALANCE_QUARTER_MONTHS[fields.quarter]
    // day 0 of the next month = last day of endMonth
    return { start: new Date(year, startMonth, 1), end: new Date(year, endMonth + 1, 0), label: `${fields.quarter} ${year}` }
  }

  if (periodType === 'Monthly') {
    const monthIndex = TRIAL_BALANCE_MONTHS.indexOf(fields.month)
    if (monthIndex >= 0) {
      return { start: new Date(year, monthIndex, 1), end: new Date(year, monthIndex + 1, 0), label: `${fields.month} ${year}` }
    }
  }

  return { start: null, end: null, label: '' }
}

export const formatForTrialBalance = (fields: Record<string, any>) => {
  const { start, end, label } = resolveTrialBalancePeriod(fields)
  if (!start || !end) return ''

  const range = `${format(start, 'MMM dd, yyyy')} - ${format(end, 'MMM dd, yyyy')}`
  return label && label !== 'Custom' ? `Period: ${label} (${range})` : `Period: ${range}`
}

export const TRIAL_BALANCE_SELECTION = 'trialBalance'

// Compare With options that reach into the year before the selected period.
const YEAR_OVER_YEAR_COMPARISONS = ['same period last year', 'full last year']

// The API refuses a Trial Balance comparison that starts before 2026: there are no reliable balances
// before then (HCK-271). Year-over-year options therefore only make sense for 2027 periods onward.
const FIRST_YEAR_WITH_COMPARABLE_LAST_YEAR = 2027

/** The year the Trial Balance period starts in, or null until it has been chosen. */
const trialBalanceStartYear = (fields: Record<string, any>): number | null => {
  const raw = fields.periodType === 'Custom Period' ? String(fields.customPeriod?.start ?? '').slice(0, 4) : fields.year
  return Number(raw) || null
}

/**
 * The options an enum filter offers for the current filters. Only the Trial Balance's Compare With
 * narrows: until the selected period starts in 2027 or later, "same period last year" and
 * "full last year" would compare with 2025 and are not offered; Custom Period still compares one
 * 2026 period with another.
 */
export const availableEnumOptions = (
  reportSelection: string | undefined,
  field: SelectionField,
  fields: Record<string, any>
): string[] => {
  const options = (field.options ?? []).filter((option): option is string => !!option)
  if (reportSelection !== TRIAL_BALANCE_SELECTION || field.field !== 'compareWith') return options

  const year = trialBalanceStartYear(fields)
  if (year !== null && year >= FIRST_YEAR_WITH_COMPARABLE_LAST_YEAR) return options

  return options.filter(option => !YEAR_OVER_YEAR_COMPARISONS.includes(option))
}

export const getReportingPeriod = (reportSelection: string, fields: Record<string, any>) => {
  const reportType = reportSelection?.toLowerCase()

  switch (reportType) {
    case 'balancesheet':
      return fields.asOfDate?.lte
    case 'incomestatement':
      return fields.dateRange?.end
    case 'trialbalance':
      return resolveTrialBalancePeriod(fields).end ?? undefined
    // Data Issues reports are exception listings over a window, so the period is the end of the
    // range the user picked rather than an as-of date.
    case 'payabledataissues':
    case 'trustdataissues':
      return fields.issueDate?.end
    default:
      return ''
  }
}

/**
 * Narrows the collected filter values to the fields the selected report actually declares.
 *
 * Filter state can outlive the report it belongs to. A DateRangePicker re-seeds itself
 * whenever its value goes missing (so the displayed range always matches what is sent —
 * see DateRangePicker.reportSwitch.test.tsx), which means that on a report switch the
 * OUTGOING picker can write its key back just as the parent clears filters, and then
 * unmount. The key is left behind, and the API rejects the request with
 * "Invalid selection fields: openDate" if the new report has no such field.
 *
 * Filtering at the payload boundary fixes this for every field type at once and does not
 * depend on the order React happens to run parent and child effects in — which is what
 * made the original bug and this one so easy to get wrong.
 */
export const pickDeclaredSelectionFields = (
  filterValues: Record<string, any>,
  declaredFields: ({ field?: string | null; visibilityRules?: SelectionField['visibilityRules'] } | null)[] | undefined
): Record<string, any> => {
  const byName = new Map(
    (declaredFields ?? []).filter((f): f is NonNullable<typeof f> => Boolean(f?.field)).map(f => [f.field as string, f])
  )
  return Object.fromEntries(
    Object.entries(filterValues ?? {}).filter(([key]) => {
      const field = byName.get(key)
      if (!field) return false // not declared by this report
      // Also drop values for fields currently HIDDEN by their visibility rules
      return isSelectionFieldVisible(field as SelectionField, filterValues)
    })
  )
}

/**
 * AR2CA (A/R Aging by Matter with Notes) follows each matter's invoices with one row holding
 * all of its billing notes: "Notes (<matter #>)" in the first column, the text in the second,
 * the rest blank. The preview spans that text across the row, as the PDF does.
 */
export const isSpannedNotesRow = (reportSelection: string | undefined, row: Record<string, unknown>) => {
  if (reportSelection !== 'arAgingByMatterWithNotes') return false

  const [label, text] = Object.values(row)
  return (label === 'Notes' || (typeof label === 'string' && label.startsWith('Notes ('))) && !!text
}

export const shouldHighlightLastRow = (reportSelection: string | undefined) => {
  if (!reportSelection) return false

  return (
    reportSelection.toLowerCase() === 'trialbalance' ||
    reportSelection.toLowerCase() === 'balancesheet' ||
    reportSelection.toLowerCase() === 'incomestatement' ||
    reportSelection.toLowerCase() === 'listofbillsbymatter' ||
    reportSelection.toLowerCase() === 'listofbillsbyattorney' ||
    // AR5A closes with a Grand Total row, same as the reports above.
    reportSelection.toLowerCase() === 'cashreceiptsbyresponsible' ||
    // BL5 closes with a Total row (bill count + summed balances).
    reportSelection.toLowerCase() === 'smalloutstandingbalances' ||
    // Matter List by Attorney / by Court close with a Grand Total row (matter count).
    reportSelection.toLowerCase() === 'caselistbyattorney' ||
    reportSelection.toLowerCase() === 'matterlistbycourt' ||
    // AR6C closes with a Grand Total row after its per-law-type subtotals.
    reportSelection.toLowerCase() === 'revenuebylawtype'
  )
}
